import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { Copy, Download, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@bookdental/ui";
import type { ConfigurarCatalogoPublicoInput } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { baixarQrDoLink, linkDoCatalogo, qrDoLink } from "@/lib/link-do-catalogo";

/**
 * Configurações › Perfil público: o link público do catálogo (o mesmo do QR na etiqueta). Ligar gera
 * o link; "Gerar link novo" derruba o antigo (e os QRs já impressos com ele). Preço é opcional.
 */
export function CatalogoPublicoConfig({ podeEditar }: { podeEditar: boolean }) {
  const utils = trpc.useUtils();
  const config = trpc.catalogoPublico.configuracao.useQuery();
  const salvar = trpc.catalogoPublico.configurar.useMutation({
    onSuccess: (r) => {
      utils.catalogoPublico.configuracao.setData(undefined, r);
      utils.catalogoPublico.configuracao.invalidate();
    },
    onError: (e) => toast.error(e.message || "Não foi possível salvar."),
  });
  const { hash } = useLocation();
  const secao = useRef<HTMLElement>(null);
  // "Configurar catálogo público" (Catálogo) chega com #catalogo-publico: o quadro fica no fim da seção.
  useEffect(() => {
    if (hash === "#catalogo-publico" && config.data) secao.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [hash, config.data]);
  if (!config.data) return null;
  const { codigo, comPreco } = config.data;
  const link = codigo ? linkDoCatalogo(codigo) : null;
  const mudar = (input: ConfigurarCatalogoPublicoInput, aviso: string) => salvar.mutate(input, { onSuccess: () => toast.success(aviso) });

  const baixarQr = () => link && baixarQrDoLink(link);

  return (
    <section ref={secao} id="catalogo-publico" className="scroll-mt-24 space-y-4 rounded-lg border border-zinc-200 p-4 dark:border-border">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-zinc-900 dark:text-foreground">Catálogo público</h3>
          <p className="mt-1 text-[12.5px] text-zinc-500 dark:text-muted-foreground">
            Um link sem login com os serviços que você oferece — para divulgar e para o QR da etiqueta que vai junto com o trabalho.
          </p>
        </div>
        {salvar.isPending && <Loader2 className="h-4 w-4 animate-spin text-brand-600" />}
        <Switch
          checked={!!codigo}
          disabled={!podeEditar || salvar.isPending}
          onCheckedChange={(ativo) => mudar({ ativo }, ativo ? "Link do catálogo ativado." : "Link do catálogo desativado.")}
          aria-label="Ativar o link público do catálogo"
        />
      </div>

      {link && (
        <div className="flex flex-col gap-4 sm:flex-row">
          <div
            className="h-32 w-32 shrink-0 self-center rounded-lg border border-zinc-200 bg-white p-1 dark:border-border [&>svg]:h-full [&>svg]:w-full"
            dangerouslySetInnerHTML={{ __html: qrDoLink(link, { celula: 4, margem: 2 }) }}
          />
          <div className="min-w-0 flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <input readOnly value={link} aria-label="Link do catálogo" className="h-9 min-w-0 flex-1 rounded-md border border-zinc-200 bg-zinc-50 px-3 text-sm text-zinc-700 dark:border-border dark:bg-muted dark:text-foreground" />
              <button
                type="button"
                onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Link copiado."))}
                className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 dark:border-border dark:bg-card dark:text-foreground"
              >
                <Copy className="h-4 w-4" />
                Copiar
              </button>
            </div>
            <div className="flex flex-wrap gap-2 text-sm">
              <a href={link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium text-brand-700 hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10">
                <ExternalLink className="h-4 w-4" />
                Abrir
              </a>
              <button type="button" onClick={baixarQr} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium text-brand-700 hover:bg-brand-50 dark:text-primary dark:hover:bg-primary/10">
                <Download className="h-4 w-4" />
                Baixar QR
              </button>
              {podeEditar && (
                <button
                  type="button"
                  disabled={salvar.isPending}
                  onClick={() => {
                    if (window.confirm("Gerar um link novo? O link atual e os QRs já impressos com ele param de abrir o catálogo.")) {
                      mudar({ novoCodigo: true }, "Link novo gerado.");
                    }
                  }}
                  className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-medium text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted"
                >
                  <RefreshCw className="h-4 w-4" />
                  Gerar link novo
                </button>
              )}
            </div>
            <label className="flex items-center gap-3 text-sm text-zinc-700 dark:text-foreground">
              <Switch
                checked={comPreco}
                disabled={!podeEditar || salvar.isPending}
                onCheckedChange={(v) => mudar({ comPreco: v }, v ? "Os preços agora aparecem no link." : "Os preços saíram do link.")}
              />
              Mostrar preços no link (tabela base, nunca o preço de um cliente)
            </label>
          </div>
        </div>
      )}
    </section>
  );
}
